import { ProLayout } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Badge, Button, Space, Tooltip, Typography } from "antd";
import { Bell, Boxes, Coins, CreditCard, Crown, FileCheck2, FileText, Gauge, GitMerge, KeyRound, ListChecks, LogOut, MailCheck, Megaphone, MessageSquareText, Settings, ShoppingBag, SlidersHorizontal, Sparkles, Store, Tags, UserRoundX, Users, ReceiptText, Webhook } from "lucide-react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useAdminRuntime } from "../app/AdminRuntime";
import { useOptionalSettingsApi } from "../modules/settings/store/SettingsStore";
import { BrandLogo } from "../shared/branding/BrandLogo";
import "./AppShell.css";

import storageIcon from "../../../iconPark/数据库设置_database-setting.svg";

type ModuleCopy = { name: string; hint: string };

const moduleCopy: Array<{ prefix: string; copy: ModuleCopy }> = [
  { prefix: "/settings/storage", copy: { name: "云存储", hint: "多云配置与默认上传存储" } },
  { prefix: "/settings/ai", copy: { name: "AI 模型", hint: "供应商、密钥与按应用授权" } },
  { prefix: "/settings/sms", copy: { name: "短信配置", hint: "通道凭据与发送封印" } },
  { prefix: "/settings/login", copy: { name: "登录配置", hint: "登录方式与安全策略" } },
  { prefix: "/settings/configurations", copy: { name: "配置中心", hint: "应用可下发的公开配置" } },
  { prefix: "/settings", copy: { name: "平台设置", hint: "运营配置与审计追踪" } },
  { prefix: "/notifications/channels", copy: { name: "渠道配置", hint: "站内信已接通，外部渠道保持封印" } },
  { prefix: "/notifications/templates", copy: { name: "模板与事件", hint: "通知模板、发布与双语文案" } },
  { prefix: "/notifications/subscriptions", copy: { name: "订阅规则", hint: "用户订阅与渠道封印规则" } },
  { prefix: "/notifications/deliveries", copy: { name: "消息与投递", hint: "站内信投递与阅读状态" } },
  { prefix: "/notifications", copy: { name: "通知中心", hint: "模板、渠道、订阅与投递" } },
  { prefix: "/content/announcements", copy: { name: "应用公告", hint: "应用公告下发与生效窗口" } },
  { prefix: "/content/consents", copy: { name: "同意记录", hint: "协议版本与用户同意回执" } },
  { prefix: "/content", copy: { name: "内容管理", hint: "协议文档、版本与审核发布" } },
  { prefix: "/points", copy: { name: "积分", hint: "按应用隔离的余额、明细与调账" } },
  { prefix: "/membership/", copy: { name: "会员与虚拟资产", hint: "共享权益、虚拟支付与资产流水" } },
  { prefix: "/memberships", copy: { name: "会员中心", hint: "等级价格、授权组与到期时间" } },
  { prefix: "/orders", copy: { name: "订单中心", hint: "业务订单、状态机与按应用查询" } },
  { prefix: "/payments/configurations/channels", copy: { name: "M3 支付中心", hint: "微信支付与支付宝商户配置" } },
  { prefix: "/payments/configurations", copy: { name: "M3 支付中心", hint: "小程序虚拟支付与商户渠道" } },
  { prefix: "/payments/reconciliation", copy: { name: "M3 支付中心", hint: "账期汇总、差异与导出" } },
  { prefix: "/payments/notifications", copy: { name: "M3 支付中心", hint: "验签、重放防护与事件审计" } },
  { prefix: "/payments", copy: { name: "M3 支付中心", hint: "订单、渠道与支付状态" } },
  { prefix: "/users", copy: { name: "M2 用户中心", hint: "用户概览、列表与账号安全" } },
  { prefix: "/dashboard", copy: { name: "运营驾驶舱", hint: "经营概况、待办与审计入口" } },
  { prefix: "/apps", copy: { name: "M1 应用管理", hint: "应用、配置与访问凭证" } },
];

function currentModule(pathname: string): ModuleCopy {
  return moduleCopy.find((item) => pathname.startsWith(item.prefix))?.copy
    ?? { name: "M1 应用管理", hint: "应用、配置与访问凭证" };
}

function selectedMenuPath(pathname: string): string {
  if (pathname === "/payments/configurations" || pathname === "/payments/configurations/") {
    return "/payments/configurations";
  }
  if (pathname.startsWith("/payments/configurations/")) {
    return "/payments/configurations/channels";
  }
  return pathname;
}

const route = {
  path: "/",
  routes: [
    { path: "/dashboard", name: "运营驾驶舱", icon: <Gauge size={18} /> },
    { path: "/apps", name: "应用管理", icon: <Boxes size={18} /> },
    { path: "/users", name: "用户中心", icon: <Users size={18} />, routes: [
      { path: "/users/overview", name: "用户概览", icon: <Gauge size={16} /> },
      { path: "/users/list", name: "用户列表", icon: <Users size={16} /> },
      { path: "/users/merge", name: "账号合并", icon: <GitMerge size={16} /> },
      { path: "/users/security", name: "登录安全", icon: <KeyRound size={16} /> },
      { path: "/users/tags", name: "用户标签", icon: <Tags size={16} /> },
      { path: "/users/deletion", name: "注销申请", icon: <UserRoundX size={16} /> },
    ] },
    { path: "/orders", name: "订单中心", icon: <ShoppingBag size={18} /> },
    { path: "/memberships", name: "会员中心", key: "memberships-menu", icon: <Crown size={18} />, routes: [
      { path: "/memberships", name: "会员", key: "memberships-page", icon: <Crown size={16} /> },
      { path: "/points", name: "积分", key: "points-page", icon: <Coins size={16} /> },
      { path: "/membership/pools", name: "权益与资产", icon: <Boxes size={16} /> },
      { path: "/membership/bindings", name: "应用关联", hideInMenu: true },
      { path: "/membership/virtual", name: "小程序虚拟支付", hideInMenu: true },
      { path: "/membership/products", name: "商品管理", hideInMenu: true },
      { path: "/membership/accounts", name: "用户资产", hideInMenu: true },
      { path: "/membership/purchases", name: "购买记录", hideInMenu: true },
      { path: "/membership/ledger", name: "资产流水", hideInMenu: true },
      { path: "/membership/audit", name: "操作审计", hideInMenu: true },
    ] },
    { path: "/payments", name: "支付中心", icon: <CreditCard size={18} />, routes: [
      { path: "/payments/configurations", name: "小程序虚拟支付", icon: <SlidersHorizontal size={16} /> },
      { path: "/payments/configurations/channels", name: "微信支付 / 支付宝", icon: <Store size={16} /> },
      { path: "/payments/orders", name: "支付订单", icon: <ReceiptText size={16} /> },
      { path: "/payments/reconciliation", name: "对账批次", icon: <FileCheck2 size={16} /> },
      { path: "/payments/notifications", name: "通知事件", icon: <Webhook size={16} /> },
    ] },
    { path: "/content", name: "内容管理", icon: <FileText size={18} />, routes: [
      { path: "/content/documents", name: "协议文档", icon: <FileText size={16} /> },
      { path: "/content/announcements", name: "应用公告", icon: <Megaphone size={16} /> },
      { path: "/content/consents", name: "同意记录", icon: <FileCheck2 size={16} /> },
    ] },
    { path: "/notifications", name: "通知中心", icon: <Bell size={18} />, routes: [
      { path: "/notifications/templates", name: "模板与事件", icon: <FileText size={16} /> },
      { path: "/notifications/channels", name: "渠道配置", icon: <SlidersHorizontal size={16} /> },
      { path: "/notifications/subscriptions", name: "订阅规则", icon: <MailCheck size={16} /> },
      { path: "/notifications/deliveries", name: "消息与投递", icon: <ListChecks size={16} /> },
    ] },
    { path: "/settings", name: "平台设置", icon: <Settings size={18} />, routes: [
      { path: "/settings/general", name: "基础设置", icon: <Settings size={16} /> },
      { path: "/settings/configurations", name: "配置中心", icon: <SlidersHorizontal size={16} /> },
      { path: "/settings/login", name: "登录配置", icon: <KeyRound size={16} /> },
      { path: "/settings/sms", name: "短信配置", icon: <MessageSquareText size={16} /> },
      { path: "/settings/storage", name: "云存储", icon: <img src={storageIcon} width={16} height={16} alt="" /> },
      { path: "/settings/ai", name: "AI 模型", icon: <Sparkles size={16} /> },
    ] },
  ],
};

export function AppShell() {
  const location = useLocation();
  const runtime = useAdminRuntime();
  const settingsApi = useOptionalSettingsApi();
  const settings = useQuery({ queryKey: ["platform-settings"], queryFn: () => settingsApi!.get(), enabled: !!settingsApi, retry: false });
  const module = currentModule(location.pathname);

  return (
    <ProLayout
      className="yahshi-shell"
      title={settings.data?.settings.consoleName ?? "Yahshi 控制台"}
      logo={<BrandLogo />}
      route={route}
      location={{ pathname: location.pathname }}
      menuProps={{ selectedKeys: [selectedMenuPath(location.pathname)] }}
      layout="mix"
      splitMenus={false}
      fixedHeader
      fixSiderbar
      siderWidth={224}
      contentWidth="Fluid"
      menuItemRender={(item, dom) =>
        item.disabled ? <span aria-disabled="true">{dom}</span> : (
          <Link aria-label={typeof item.name === "string" ? item.name : undefined} to={item.path ?? "/dashboard"}>{dom}</Link>
        )
      }
      actionsRender={() => [
        <Space key="environment" size={8} className="environment-indicator">
          <Badge status="processing" />
          <Typography.Text>{runtime.mode === "cloud" ? "ECS 环境" : "本地开发"}</Typography.Text>
        </Space>,
        ...(runtime.username ? [<Typography.Text key="username" className="runtime-username">{runtime.username}</Typography.Text>] : []),
        ...(runtime.signOut ? [<Tooltip key="sign-out" title="退出登录"><Button type="text" aria-label="退出登录" icon={<LogOut size={17} aria-hidden="true" />} onClick={() => void runtime.signOut?.()} /></Tooltip>] : []),
      ]}
      menuFooterRender={() => (
        <div className="module-gate">
          <span>当前模块</span>
          <strong>{module.name}</strong>
          <small>{module.hint}</small>
        </div>
      )}
    >
      <a className="skip-link" href="#main-content">跳到主要内容</a>
      <main id="main-content" className="yahshi-content" tabIndex={-1}>
        <Outlet />
      </main>
    </ProLayout>
  );
}
